import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserProgress } from "@/hooks/useUserProgress";
import { getCurrencySymbol as getCurrencySymbolUtil } from "@/utils/currencies";
import { getTransactionsByCohortId } from "@/services/auth";

interface Transaction {
  id: number;
  date: string;
  details: string;
  amount: string;
  cohortId: string;
  cohortName: string;
  includeTransaction?: boolean;
  includeStatement?: boolean;
  includeCurrent?: boolean;
  includeDirect?: boolean;
}

const EMPTY_TRANSACTIONS: Transaction[] = [];

export const useTransactions =(cohortId?: string) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usedAmounts, setUsedAmounts] = useState<Set<string>>(new Set());
  // Which transaction set (primary/custom) `transactions` was loaded for.
  const [transactionsViewMode, setTransactionsViewMode] = useState<
    "default" | "custom" | null
  >(null);
  const loadRequestRef = useRef(0);
  const { getTransactions, currentUser } = useAuth();
  const {
    currentTransactionIndex: defaultTransactionIndex,
    customTransactionIndex,
    setTransactionIndex,
    isLoaded: progressLoaded,
    accountDroppedAmounts,
    liabilityDroppedAmounts,
    equityAccountDroppedAmounts,
    expenseDroppedAmounts,
    incomeDroppedAmounts,
    equityAmounts,
    viewMode,
  } = useUserProgress(cohortId);

  // Compute the actual current transaction index based on view mode
  const currentTransactionIndex =
    viewMode === "custom" ? customTransactionIndex : defaultTransactionIndex;

  // Update used amounts whenever dropped amounts change
  useEffect(() => {
    const newUsedAmounts = new Set<string>();

    // Check all account types for used amounts
    const checkDroppedAmounts = (droppedAmountsMap: Record<string, any[]>) => {
      Object.values(droppedAmountsMap).forEach((amounts) => {
        amounts.forEach((amount) => {
          const amountId = `${amount.type}-${amount.transactionId}`;
          newUsedAmounts.add(amountId);
        });
      });
    };

    // Check all containers for used amounts
    checkDroppedAmounts(accountDroppedAmounts);
    checkDroppedAmounts(liabilityDroppedAmounts);
    checkDroppedAmounts(equityAccountDroppedAmounts);
    checkDroppedAmounts(expenseDroppedAmounts || {});
    checkDroppedAmounts(incomeDroppedAmounts || {});

    // Check equity amounts
    equityAmounts.forEach((amount) => {
      const amountId = `${amount.type}-${amount.transactionId}`;
      newUsedAmounts.add(amountId);
    });

    setUsedAmounts(newUsedAmounts);
  }, [
    accountDroppedAmounts,
    liabilityDroppedAmounts,
    equityAccountDroppedAmounts,
    expenseDroppedAmounts,
    incomeDroppedAmounts,
    equityAmounts,
  ]);

  // Currency symbols mapping
  const getCurrencySymbol = (currency?: string) => {
    return getCurrencySymbolUtil(currency);
  };

  // Format amount with currency
  const formatAmount = (amount: string | number) => {
    const symbol = getCurrencySymbol(currentUser?.currency);
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

    // Always show absolute value without + or - signs
    return `${symbol}${Math.abs(numAmount).toLocaleString()}`;
  };

  // Load transactions
  const loadTransactions = async () => {
    // Switching Primary/Custom can start several loads back to back; only the
    // most recent one may write state, or a slow earlier response would put
    // the other set's transactions back in the list.
    const requestId = ++loadRequestRef.current;
    const requestedViewMode = viewMode;
    try {
      setLoading(true);
      setError(null);

      // If cohortId is provided (trainer joining course), load directly from cohort.
      // viewMode is passed explicitly so a participant who switched back to the
      // primary set gets those transactions, not the cohort-wide setting.
      console.log("[useTransactions] loading, cohortId:", cohortId, "viewMode:", requestedViewMode);
      const userTransactions = cohortId
        ? await getTransactionsByCohortId(cohortId, requestedViewMode)
        : await getTransactions(requestedViewMode);
      if (requestId !== loadRequestRef.current) return;
      console.log("[useTransactions] loaded", userTransactions.length, "transactions");

      setTransactions(userTransactions);
      setTransactionsViewMode(requestedViewMode);

      // Don't reset transaction index if we have saved progress
      if (userTransactions.length > 0 && !progressLoaded) {
        setTransactionIndex(0);
      }
    } catch (err: any) {
      if (requestId !== loadRequestRef.current) return;
      console.error("Error loading transactions:", err);
      setError(err.message || "Failed to load transactions");
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  };
  // The cohortViewModeChanged listener below is registered once, so it calls
  // through this ref to always load with the current view mode.
  const loadTransactionsRef = useRef(loadTransactions);
  loadTransactionsRef.current = loadTransactions;

  // Until the reload for a new view mode lands, `transactions` still holds the
  // other set. Treat that as loading so nothing shows (or clamps the index
  // against) the wrong set's transactions.
  const isStaleSet =
    transactionsViewMode !== null && transactionsViewMode !== viewMode;
  const activeTransactions = isStaleSet ? EMPTY_TRANSACTIONS : transactions;

  // Navigation functions
  const goToNext = () => {
    if (currentTransactionIndex < activeTransactions.length - 1) {
      const newIndex = currentTransactionIndex + 1;
      setTransactionIndex(newIndex);
    }
  };

  const goToPrevious = () => {
    if (currentTransactionIndex > 0) {
      setTransactionIndex(currentTransactionIndex - 1);
    }
  };

  // Jump straight to a transaction — used by the sidebar's transaction picker.
  const goToIndex = (index: number) => {
    if (
      Number.isInteger(index) &&
      index >= 0 &&
      index < activeTransactions.length &&
      index !== currentTransactionIndex
    ) {
      setTransactionIndex(index);
    }
  };

  // A saved index can point past the end of the list (transactions removed
  // after progress was saved, or an index carried over from another course).
  // Clamp it so the transaction panel doesn't show "No Transactions".
  useEffect(() => {
    if (
      !loading &&
      !isStaleSet &&
      activeTransactions.length > 0 &&
      currentTransactionIndex >= activeTransactions.length
    ) {
      setTransactionIndex(activeTransactions.length - 1);
    }
  }, [loading, isStaleSet, activeTransactions.length, currentTransactionIndex]);

  // Get current transaction (reactive)
  const currentTransaction = useMemo(() => {
    return activeTransactions[currentTransactionIndex] || null;
  }, [activeTransactions, currentTransactionIndex]);

  // Check if navigation is possible (reactive)
  const canGoNext = useMemo(() => {
    return currentTransactionIndex < activeTransactions.length - 1;
  }, [currentTransactionIndex, activeTransactions.length]);

  const canGoPrevious = useMemo(() => {
    return currentTransactionIndex > 0;
  }, [currentTransactionIndex]);

  // Generate draggable amounts from transaction
  const getDraggableAmounts = () => {
    const transaction = currentTransaction;
    if (!transaction) return [];

    const amount = parseFloat(transaction.amount);

    // Always return both debit and credit options for the transaction
    return [
      {
        id: `debit-${transaction.id}`,
        amount: amount,
        type: "debit",
        color: "sky",
        date: transaction.date,
        details: transaction.details,
        transactionId: transaction.id, // Include transactionId for snapshot filtering
        isPositive: true, // Debit amounts increase asset/expense balances
      },
      {
        id: `credit-${transaction.id}`,
        amount: amount,
        type: "credit",
        color: "red",
        date: transaction.date,
        details: transaction.details,
        transactionId: transaction.id, // Include transactionId for snapshot filtering
        isPositive: false, // Credit amounts decrease asset/expense balances
      },
    ];
  };

  // Load transactions on mount and when view mode changes
  useEffect(() => {
    console.log("[useTransactions] effect check - currentUser:", !!currentUser, "progressLoaded:", progressLoaded, "cohortId:", cohortId);
    if (currentUser && progressLoaded) {
      loadTransactions().catch(() => {
        // Error already handled in loadTransactions via setError
      });
    }
  }, [currentUser, progressLoaded, viewMode, cohortId]);

  // Listen for view mode change events to ensure immediate updates
  useEffect(() => {
    const handleViewModeChange = (event: CustomEvent) => {
      if (currentUser && progressLoaded) {
        loadTransactionsRef.current();
      }
    };

    window.addEventListener(
      "cohortViewModeChanged",
      handleViewModeChange as EventListener,
    );

    return () => {
      window.removeEventListener(
        "cohortViewModeChanged",
        handleViewModeChange as EventListener,
      );
    };
  }, [currentUser, progressLoaded]);

  return {
    transactions: activeTransactions,
    currentTransactionIndex,
    currentTransaction,
    loading: loading || isStaleSet,
    error,
    canGoNext,
    canGoPrevious,
    goToNext,
    goToPrevious,
    goToIndex,
    formatAmount,
    getDraggableAmounts,
    loadTransactions,
  };
};
